'use client';

import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Booking, Invitee } from '@/types/booking';
import { Textarea } from '@/components/ui/textarea';
import { useState } from 'react';
import { toast } from 'sonner';
import { apiService, ApiEndpoint } from '@/lib/api';
import { BookingInfo } from './booking-info';
import { Card, CardContent } from '../../components/ui/card';

interface BookingCancelProps {
  resource: unknown;
  booking: Booking;
  onBack: () => void;
}

export function BookingCancel({
  onBack,
  resource,
  booking,
}: BookingCancelProps) {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const invitee = booking.invitees ? booking.invitees[0] : ({} as Invitee);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleCancel(reason);
  };

  const handleCancel = async (reason: string) => {
    try {
      setIsSubmitting(true);
      await apiService.put(
        ApiEndpoint.bookings,
        `${invitee.invitee_id}_${invitee.token}/cancel`,
        {
          cancel_reason: reason,
        }
      );
    } catch (err: any) {
      toast.error(err.message || 'Something went wrong, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">Cancel Booking</h2>
          <p className="text-muted-foreground">{booking.subject}</p>
        </div>
        <Card className="mb-8">
          <CardContent className="pt-6">
            <BookingInfo booking={booking}></BookingInfo>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="reason" className="text-sm font-medium">
              Cancellation Reason
            </label>
            <Textarea
              id="reason"
              placeholder="Please provide a reason for cancellation..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[120px]"
              required
            />
          </div>

          <div className="flex gap-4">
            <Button type="submit" variant="destructive" disabled={isSubmitting}>
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Cancel Booking
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
